import { ROUTE_PATHS } from '@constants/routes';
import { buildProjectPath, type ProjectBase } from '@lib/routing/projectPath';
import { folderPathOf, useGetProjectQuery } from '@store/api/projectEndpoints';
import { Navigate, useParams } from 'react-router-dom';

interface LegacyProjectRedirectProps {
  base: ProjectBase;
}

/**
 * Resolves an id-only project URL to its path-bearing form. The tail is carried over
 * verbatim, so every tab and inspection deep link survives. A project that cannot be
 * resolved still redirects — without a folder prefix — and the destination page reports
 * the error, rather than this route reporting it a second time.
 */
const LegacyProjectRedirect: React.FC<LegacyProjectRedirectProps> = ({ base }) => {
  const { projectId, '*': tail } = useParams();
  const { data: project, isError } = useGetProjectQuery(
    { id: projectId!, showError: false }, { skip: !projectId }
  );

  if (!projectId) return <Navigate replace to={ROUTE_PATHS.FOLDERS} />;
  if (!project && !isError) return null;

  const target = buildProjectPath({ base, folderPath: folderPathOf(project), projectId });

  return <Navigate replace to={tail ? `${target}/${tail}` : target} />;
};

export default LegacyProjectRedirect;
